import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import i18nZipcodes from 'i18n-zipcodes';
import {
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { CountryService } from '../services/country.service';

export const EMAIL_REGEXP =
  /^(?!.*\.{2})(\s*)([\w-][\w%.-]*[\w-]|[\w-])@[\d.A-Za-z-]+\.[A-Za-z]{2,}(\s*)$/;
const NUMBER_REGEXP = /^(-?\d+)(,\d+)?$/;

export function ValidateCountryCode(
  control: AbstractControl,
  alphaCode: string,
) {
  if (
    control.value &&
    !CountryService.getAllCountries(alphaCode)
      .map((c) => c.label.toLowerCase())
      .includes(control.value.toLowerCase())
  ) {
    return { validCountry: true };
  }
}

export function MinOptionsDropDown(min: number) {
  return (control: AbstractControl) => {
    if (control.value) {
      const parsed = JSON.parse(control.value);
      const underMin = parsed?.length < min;
      return underMin ? { underMin: true } : undefined;
    }
  };
}

export function ValidateMaxOneYear(oldDate: Date) {
  return (control: AbstractControl) => {
    if (control.value && oldDate) {
      const controlDate = new Date(control.value);
      controlDate.setHours(0, 0, 0, 0);
      const oneYearLater = new Date(oldDate);
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
      return controlDate > oneYearLater ? { oneYearLater: true } : undefined;
    }
  };
}

export function ValidateMaxSixMonths(oldDate: Date, projectDate = false) {
  return (control: AbstractControl) => {
    if (control.value && oldDate) {
      const controlDate = new Date(control.value);
      controlDate.setHours(0, 0, 0, 0);
      const sixMonthsLater = new Date(oldDate);
      sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
      return controlDate > sixMonthsLater
        ? projectDate
          ? { sixMonthsProjectEnd: true }
          : { sixMonthsLater: true }
        : undefined;
    }
  };
}

export function ValidateMaxDate(maxDate: Date) {
  return (control: AbstractControl) => {
    if (control.value && maxDate) {
      const controlDate = new Date(control.value);
      controlDate.setHours(0, 0, 0, 0);
      return controlDate >= maxDate ? { maxDate: true } : undefined;
    }
  };
}

export function ValidateMinDateBegaran(minDate: Date, controlName: string) {
  return (control: AbstractControl) => {
    if (control.value && minDate) {
      const controlDate = new Date(control.value);
      controlDate.setHours(0, 0, 0, 0);
      if (controlName === 'projekt') {
        return controlDate < minDate ? { minDateProjekt: true } : undefined;
      } else {
        return controlDate < minDate ? { minDateReport: true } : undefined;
      }
    }
  };
}

export function ValidateIsValidDiaryNumber(control: AbstractControl) {
  if (control.value && !/^\d{4}-\d{5}$/.test(control.value)) {
    return { validDiaryNumber: true };
  }
}

export function ValidateIsValidPhoneNumber(control: AbstractControl) {
  if (control.value) {
    const valuePhoneNumber = control.value.toString();
    if (isValidPhoneNumber(valuePhoneNumber)) {
      return;
    }

    return { validPhoneNumber: true };
  }
}

export function ValidatePhoneNumber(control: AbstractControl) {
  const valueWithoutSpace = control.value
    ? control.value.replaceAll(' ', '')
    : '';

  if (!valueWithoutSpace) {
    return;
  }

  const parsedNumber = parsePhoneNumberFromString(valueWithoutSpace);

  return parsedNumber && parsedNumber.isPossible()
    ? undefined
    : { validPhoneNumber: true };
}

export function ValidateCountryCallingCode(control: AbstractControl) {
  const valueWithoutSpace = control.value
    ? control.value.replaceAll(' ', '')
    : '';
  if (valueWithoutSpace != '' && /^\+0/g.test(valueWithoutSpace)) {
    return { validCountryCallingCode: true };
  }
}

export function ValidatePostalCode(control: AbstractControl) {
  if (control.value && !/^\d{3} \d{2}$/.test(control.value)) {
    return { validPostalCode: true };
  }
}

export function ValidatePostalCodeByCountry(control: AbstractControl) {
  if (
    !control.value ||
    !control.parent ||
    !(control.parent.value.country || control.parent.value.countryCode)
  ) {
    return;
  }

  const country =
    control.parent.value.country ?? control.parent.value.countryCode;

  if (!country) {
    return;
  }

  try {
    return i18nZipcodes(country, control.value)
      ? undefined
      : { validPostalCode: true };
  } catch {
    // krasch i i18nZipcodes, tex landskoden finns inte
    return;
  }
}

export const DateValidation = {
  endDateAfterStartDate(formGroup: UntypedFormGroup) {
    const startDate = formGroup.get('startDate');
    const endDate = formGroup.get('endDate');

    if (
      !startDate ||
      !startDate.value ||
      typeof startDate.value === 'string' ||
      !startDate.value.isValid() ||
      !endDate ||
      !endDate.value ||
      typeof endDate.value === 'string' ||
      !endDate.value.isValid()
    ) {
      return;
    }

    const isValid = startDate.value.isSameOrBefore(endDate.value);
    return isValid ? undefined : { isAfter: true };
  },
};

export function ValidateSpace(control: AbstractControl) {
  if (control.value) {
    const isWhitespace = (control.value.toString() || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? undefined : { required: true };
  }
}

export function ValidateEmail(control: AbstractControl) {
  if (control.value) {
    const email = control.value;
    const valid = EMAIL_REGEXP.test(email);
    return valid ? undefined : { validEmail: true };
  }
}

export function ValidateWebpage(control: AbstractControl) {
  if (!control.value) {
    return;
  }

  const trimmed = control.value.trim();
  if (!trimmed) {
    return;
  }

  // Limit URL length to prevent DoS
  if (trimmed.length > 2048) {
    return { validWebpage: true };
  }

  // Detect any protocol (with or without //) - reject anything that isn't http/https
  // mailto:, javascript:, data:, ftp:// etc. all match ^[a-zA-Z][a-zA-Z0-9+.-]*:
  const protocolMatch = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):(\/\/)?/);
  let urlString: string;

  if (protocolMatch) {
    const protocol = protocolMatch[1].toLowerCase();
    const hasDoubleSlash = protocolMatch[2] === '//';
    // Only allow http/https, and require :// (not just :)
    if (!['http', 'https'].includes(protocol) || !hasDoubleSlash) {
      return { validWebpage: true };
    }
    urlString = trimmed;
  } else {
    // No protocol - add https://
    urlString = `https://${trimmed}`;
  }

  try {
    const url = new URL(urlString);

    // Reject localhost and private IP ranges (SSRF prevention)
    const hostname = url.hostname.toLowerCase();
    if (
      hostname === 'localhost' ||
      /^127\./.test(hostname) ||
      /^192\.168\./.test(hostname) ||
      /^10\./.test(hostname) ||
      /^172\.(1[6-9]|2[0-9]|3[01])\./.test(hostname)
    ) {
      return { validWebpage: true };
    }

    // Require a domain with at least one dot (for TLD)
    if (!url.hostname.includes('.')) {
      return { validWebpage: true };
    }

    // Validate TLD length (must be at least 2 characters)
    const tld = url.hostname.split('.').pop() ?? '';
    if (tld.length < 2) {
      return { validWebpage: true };
    }
  } catch {
    return { validWebpage: true }; // Invalid URL format
  }
}

export function ValidateEmailDontShareWithYourself(
  control: AbstractControl,
  activeUserMail: string,
) {
  if (control.value) {
    const valid = activeUserMail.toLowerCase() !== control.value.toLowerCase();
    return valid ? undefined : { delarMedSigSjalv: true };
  }
}

export function ValidateEmailNoDuplicates(
  control: AbstractControl,
  emails: string[],
) {
  if (control.value && emails && emails.length > 0) {
    const valid = !emails.includes(control.value);
    return valid ? undefined : { epostRedanDelad: true };
  }
}

export function ValidateOrgNumber(control: AbstractControl) {
  const foreignControl = control.parent?.get('isForeign');
  if (
    control.parent &&
    foreignControl &&
    !foreignControl.value &&
    control.value &&
    (!/^(\d{6}-\d{4})$/.test(control.value) || !IsValidLuhn(control.value))
  ) {
    return { validOrgNumber: true };
  }
}

export function ValidateNumber(control: AbstractControl) {
  if (control.value) {
    const number = control.value;
    const valid = NUMBER_REGEXP.test(number);
    return valid ? undefined : { validNumber: true };
  }
}

export function ValidateClassificationRequired(control: AbstractControl) {
  if (control.value) {
    const valueClassification = JSON.parse(control.value);
    if (
      valueClassification.BO.choosenItems.length > 0 &&
      valueClassification.FO.choosenItems.length > 0 &&
      valueClassification.PO.choosenItems.length > 0
    ) {
      return;
    }
  }
  return { validClassification: true };
}

export function MaxLengthNumber(maxLength: number) {
  return (control: AbstractControl) => {
    if (control.value) {
      const number = control.value.toString();
      return number.length > maxLength ? { maxLengthNumber: true } : undefined;
    }
  };
}

export function MaxLengthTable(maxLength: number) {
  return (control: AbstractControl) => {
    if (control.value) {
      const parsed: { value?: string }[] = JSON.parse(control.value);
      const anyOverMaxLength = parsed.some(
        (a) => (a.value?.toString().length ?? 0) > maxLength,
      );
      return anyOverMaxLength ? { maxLengthTable: true } : undefined;
    }
  };
}

export function MaxLengthPrograms(maxLength: number) {
  return (control: AbstractControl) => {
    if (control.value) {
      const parsed = JSON.parse(control.value);
      const overMaxLength = parsed.length > maxLength;

      return overMaxLength ? { overMaxLength: true } : undefined;
    }
  };
}

export function IsValidLuhn(str: string): boolean {
  str = str.replace('-', '');
  let checksum = 0;
  for (let i = 0; i < str.length; i++) {
    const charIndex = str.length - 1 - i;
    const number = Number.parseInt(str.charAt(charIndex));
    if (Number.isNaN(number)) {
      return false;
    }
    if (i % 2 === 0) {
      checksum += number;
    } else {
      checksum += number * 2 > 9 ? number * 2 - 9 : number * 2;
    }
  }

  return checksum % 10 == 0;
}

export function ValidatePG(control: AbstractControl) {
  if (control.value) {
    const number = control.value;
    const valid = /^\d{1,7}-\d$/.test(number) && IsValidLuhn(number);
    return valid ? undefined : { validPG: true };
  }
}

export function ValidateBG(control: AbstractControl) {
  if (control.value) {
    const number = control.value;
    const valid = /^\d{3,4}-\d{4}$/.test(number) && IsValidLuhn(number);
    return valid ? undefined : { validBG: true };
  }
}

export function IsPersonnummer(str: string) {
  str = str.replace('-', '');
  // Den tredje siffran är antingen 0 eller 1 på personnummer
  return IsValidLuhn(str) && parseInt(str.slice(-8, -7)) < 2;
}

export function ValidateSwift(control: AbstractControl) {
  if (control.value) {
    const swift = control.value;
    const valid = /^[A-Z]{6}[\dA-Z]{2}([\dA-Z]{3})?$/.test(swift);
    return valid ? undefined : { validSwift: true };
  }
}

export function ValidateBSB(control: AbstractControl) {
  if (control.value) {
    const number = control.value;
    const valid = /^AU\d{6}$/.test(number);
    return valid ? undefined : { validBSB: true };
  }
}

export function ValidateRouting(control: AbstractControl) {
  if (control.value) {
    const number = control.value;
    const valid = /^CC0\d{8}$/.test(number);
    return valid ? undefined : { validRouting: true };
  }
}

export function ValidateFW(control: AbstractControl) {
  if (control.value) {
    const number = control.value;
    const valid = /^FW\d{9}$/.test(number);
    return valid ? undefined : { validFW: true };
  }
}

export function ValidateIban(countryCode?: string) {
  return (control: AbstractControl) => {
    if (control.value) {
      const iban = control.value as string;
      const validFormat = new RegExp(
        String.raw`^${countryCode ?? '[A-Z]{2}'}\d{2}[A-Z\d]{1,30}$`,
      ).test(iban);
      if (!validFormat) {
        return { validIban: true };
      }

      const ibanRearranged = iban.slice(4) + iban.slice(0, 4);
      let charConvertedNumber = '';
      for (let i = 0; i < ibanRearranged.length; i++) {
        const charCode = ibanRearranged.codePointAt(i) as number;
        charConvertedNumber +=
          charCode >= 65 && charCode <= 90 ? charCode - 55 : ibanRearranged[i];
      }
      const valid = BigInt(charConvertedNumber) % 97n === 1n;
      return valid ? undefined : { validIban: true };
    }
  };
}
